import { create } from 'zustand'

import {
  crmEngine,
  type CrmPermissions,
  type CrmStatistics,
  type Opportunity,
  type OpportunityFilters,
  type PipelineColumn,
  type Reminder,
  type WhatsappMessageDraft,
} from '@/services/crm'

interface OpportunityStoreState {
  ready: boolean
  opportunities: Opportunity[]
  pipeline: PipelineColumn[]
  reminders: Reminder[]
  statistics: CrmStatistics | null
  filters: OpportunityFilters
  permissions: CrmPermissions
  selectedId: string | null
  contactDraft: WhatsappMessageDraft | null
  responseOpen: boolean

  hydrate: () => void
  setFilters: (filters: Partial<OpportunityFilters>) => void
  setSelectedId: (id: string | null) => void
  setContactDraft: (draft: WhatsappMessageDraft | null) => void
  setResponseOpen: (open: boolean) => void
  setPermissions: (patch: Partial<CrmPermissions>) => void
  refresh: () => void
}

export const useOpportunityStore = create<OpportunityStoreState>((set, get) => ({
  ready: false,
  opportunities: [],
  pipeline: [],
  reminders: [],
  statistics: null,
  filters: { status: 'ALL' },
  permissions: crmEngine.getPermissions(),
  selectedId: null,
  contactDraft: null,
  responseOpen: false,

  hydrate: () => {
    crmEngine.syncFromQuotes()
    const filters = get().filters
    set({
      ready: true,
      opportunities: crmEngine.list(filters),
      pipeline: crmEngine.pipeline(filters),
      reminders: crmEngine.listReminders(),
      statistics: crmEngine.statistics(filters),
      permissions: crmEngine.getPermissions(),
    })
  },

  setFilters: (filters) => {
    const next = { ...get().filters, ...filters }
    set({
      filters: next,
      opportunities: crmEngine.list(next),
      pipeline: crmEngine.pipeline(next),
      statistics: crmEngine.statistics(next),
    })
  },

  setSelectedId: (id) => set({ selectedId: id }),
  setContactDraft: (draft) => set({ contactDraft: draft }),
  setResponseOpen: (open) => set({ responseOpen: open }),

  setPermissions: (patch) => {
    const permissions = crmEngine.setPermissions(patch)
    set({ permissions })
  },

  refresh: () => get().hydrate(),
}))
